import React, { useReducer } from "react"

import * as accordion from "shared/components/layout/Accordion"

import * as engine from "./engine"
import * as schema from "./schema"
import * as slice from "./slice"

export type Actions = {
    assignSingleAnswer(payload: { id: string; value: string }): void
    assignMultiAnswer(payload: { id: string; value: string[] }): void
    assignAccordionState(payload: {
        id: string
        state: accordion.ItemState
    }): void
    nextSection(): void
    previousSection(): void
    goToSection(sectionIndex: number): void
    nextQuestion(): void
    previousQuestion(): void
    goToQuestion(questionIndex: number): void
    reset(): void
}

export type AnswerOption = {
    id: string
    label: string
    state: "checked" | "unchecked"
}
export type AnswerOptions = {
    options: AnswerOption[]
    answered: "no" | "yes"
}

export type ExtraQuestion = {
    question: schema.CommonQuestion
    answers: AnswerOptions
}

export type Section = {
    question: schema.CommonQuestion
    itemState: accordion.ItemState
    answers: AnswerOptions
    extraQuestions: ExtraQuestion[]
}

export type ViewModel = {
    sections: Section[]
    action: Actions
    result: schema.Result[] | null
    sectionResults: Record<string, schema.Result[]>
    getSectionResults: (sectionId: string) => schema.Result[]
    sectionIndex: number
    questionIndex: number
}

function stateToFacts(state: slice.State): schema.Facts {
    return {
        ...state.singleAnswers,
        ...state.multiAnswers,
    }
}

export function useViewModel(
    schema_: schema.Schema,
    { onResults }: { onResults: (results: schema.Result[]) => void },
): ViewModel {
    const [state, dispatch] = useReducer(slice.reducer, slice.initialState)

    const sliceActions: Actions = React.useMemo(() => {
        return {
            assignSingleAnswer(payload: { id: string; value: string }): void {
                return dispatch(slice.actions.assignSingleAnswer(payload))
            },
            assignMultiAnswer(payload: { id: string; value: string[] }): void {
                return dispatch(slice.actions.assignMultiAnswer(payload))
            },
            assignAccordionState(payload: {
                id: string
                state: accordion.ItemState
            }): void {
                return dispatch(slice.actions.assignAccordionState(payload))
            },
            nextSection(): void {
                return dispatch(
                    slice.actions.setSectionIndex({
                        sectionIndex: state.sectionIndex + 1,
                    }),
                )
            },
            previousSection(): void {
                return dispatch(
                    slice.actions.setSectionIndex({
                        sectionIndex: state.sectionIndex - 1,
                    }),
                )
            },
            goToSection(sectionIndex: number): void {
                return dispatch(slice.actions.setSectionIndex({ sectionIndex }))
            },
            nextQuestion(): void {
                return dispatch(
                    slice.actions.setQuestionIndex({
                        questionIndex: state.questionIndex + 1,
                    }),
                )
            },
            previousQuestion(): void {
                return dispatch(
                    slice.actions.setQuestionIndex({
                        questionIndex: state.questionIndex - 1,
                    }),
                )
            },
            goToQuestion(questionIndex: number): void {
                return dispatch(
                    slice.actions.setQuestionIndex({ questionIndex }),
                )
            },
            reset(): void {
                return dispatch(slice.actions.reset())
            },
        }
    }, [dispatch, state.sectionIndex, state.questionIndex])

    const localEngine = React.useMemo(() => engine.make(schema_), [schema_])

    const facts = React.useMemo(() => stateToFacts(state), [state])
    const [metaFacts, setMetaFacts] = React.useState<schema.MetaFacts | null>(
        null,
    )
    const [result, setResult] = React.useState<schema.Result[]>([])
    const [sectionResults, setSectionResults] = React.useState<
        Record<string, schema.Result[]>
    >({})

    React.useEffect(() => {
        async function runEngine() {
            console.log("facts", facts)
            const { metaFacts: metaFacts_, result: result_ } =
                await localEngine.run(facts)
            setMetaFacts(metaFacts_)
            setResult(result_)
            onResults(result_)
        }

        void runEngine()
        // TODO: refactor so deps are exhaustive
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localEngine, facts])

    // Update section results when global results change
    React.useEffect(() => {
        if (result.length > 0 && metaFacts) {
            // Get the current section ID
            const currentSectionId = schema_.questions[state.sectionIndex]?.id
            if (currentSectionId) {
                // Replace results for current section instead of accumulating
                setSectionResults((prev) => ({
                    ...prev,
                    [currentSectionId]: result,
                }))
            }
        }
    }, [result, metaFacts, state.sectionIndex, schema_.questions])

    const sections: Section[] = React.useMemo(() => {
        if (!metaFacts) {
            return []
        }

        const currentQuestion = metaFacts.$nextQuestion

        function lookupItemState(sectionId: string) {
            const defaultState =
                currentQuestion === sectionId
                    ? accordion.itemState.Open
                    : accordion.itemState.Closed
            return state.accordion[sectionId] || defaultState
        }

        return schema_.questions.map((question) => ({
            question,
            itemState: lookupItemState(question.id),
            answers: calculateAnswers(question, state),
            extraQuestions: calculateExtraQuestions(
                metaFacts.$activeSubQuestions,
                state,
                question,
            ),
        }))
    }, [metaFacts, schema_.questions, state])

    const getSectionResults = React.useCallback(
        (sectionId: string): schema.Result[] => {
            return sectionResults[sectionId] || []
        },
        [sectionResults],
    )

    return {
        sections,
        action: { ...sliceActions },
        result,
        sectionResults,
        getSectionResults,
        sectionIndex: state.sectionIndex,
        questionIndex: state.questionIndex,
    } as const
}

export function calculateExtraQuestions(
    activeSubQuestions: readonly string[],
    state: slice.State,
    parentQuestion: schema.Question,
) {
    return parentQuestion.subquestions
        .filter((subquestion) => activeSubQuestions.includes(subquestion.id))
        .map((question) => ({
            question,
            answers: calculateAnswers(question, state),
        }))
}

export function calculateAnswers(
    question: schema.CommonQuestion,
    state: slice.State,
): AnswerOptions {
    switch (question.type) {
        case schema.questionType.oneAnswer: {
            const answer = state.singleAnswers[question.id]
            return {
                answered: answer ? "yes" : "no",
                options: question.options.map(
                    calculateAnswerOptions(answer ? [answer] : []),
                ),
            }
        }
        case schema.questionType.anyThatApply: {
            const answer = state.multiAnswers[question.id]
            return {
                answered: answer ? "yes" : "no",
                options: question.options.map(
                    calculateAnswerOptions(answer ?? []),
                ),
            }
        }
        default:
            throw new Error("missing case")
    }
}

export const calculateAnswerOptions =
    (answers: string[]) =>
    (value: schema.Choice): AnswerOption => ({
        id: value.id,
        label: value.title,
        state: answers.includes(value.id) ? "checked" : "unchecked",
    })
